import React, { useState, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Tag, 
  Space, 
  Typography, 
  Empty, 
  Skeleton, 
  Tooltip, 
  Progress,
  Dropdown,
  Modal,
  Input,
  message,
  Descriptions,
  Divider,
  List,
  Alert
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  FileTextOutlined, 
  CalendarOutlined,
  MoreOutlined,
  CopyOutlined,
  UserOutlined,
  InfoCircleOutlined,
  BuildOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { Questionario } from '../../../types/dashboard/mapeamentos.types';

const { Title, Text } = Typography;

interface QuestionarioListProps {
  questionarios: Questionario[];
  onEdit: (questionario: Questionario) => void;
  onDelete: (id: string) => void;
  onView: (id: string) => void;
  onCreateVersion: (id: string) => void;
  onDuplicate?: (questionarioId: string, novoNome: string) => Promise<void>;
  loading?: boolean;
  // Props para contexto de cliente
  clienteId?: string;
  clienteNome?: string;
}

const QuestionarioList: React.FC<QuestionarioListProps> = React.memo(({
  questionarios,
  onEdit,
  onDelete,
  onView,
  onCreateVersion,
  onDuplicate,
  loading = false,
  clienteId,
  clienteNome
}) => {
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedQuestionario, setSelectedQuestionario] = useState<Questionario | null>(null);
  const [novoNome, setNovoNome] = useState('');
  const [duplicating, setDuplicating] = useState(false);

  const handleDelete = useCallback((questionario: Questionario) => {
    Modal.confirm({
      title: 'Confirmar exclusão',
      content: (
        <div>
          <p>Tem certeza que deseja excluir o questionário:</p>
          <p><strong>"{questionario.nome}"</strong></p>
          {clienteId && (
            <p style={{ color: '#666', fontSize: '12px' }}>
              Cliente: {clienteNome || 'Cliente selecionado'}
            </p>
          )}
          <p>Esta ação não pode ser desfeita.</p>
        </div>
      ),
      okText: 'Sim, excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => onDelete(questionario._id)
    });
  }, [onDelete, clienteId, clienteNome]);

  const handleView = useCallback((questionario: Questionario) => {
    setSelectedQuestionario(questionario);
    setViewModalVisible(true);
  }, []);

  const handleDuplicate = useCallback((questionario: Questionario) => {
    setSelectedQuestionario(questionario);
    setNovoNome(`${questionario.nome} - Cópia`);
    setDuplicateModalVisible(true);
  }, []);

  const handleConfirmDuplicate = useCallback(async () => {
    if (!selectedQuestionario || !onDuplicate) return;

    if (!novoNome.trim()) {
      message.error('Nome é obrigatório');
      return;
    }

    setDuplicating(true);
    try {
      await onDuplicate(selectedQuestionario._id, novoNome.trim());
      setDuplicateModalVisible(false);
      setSelectedQuestionario(null);
      setNovoNome('');
      message.success('Questionário duplicado com sucesso');
    } catch (error) {
      message.error('Erro ao duplicar questionário');
    } finally {
      setDuplicating(false);
    }
  }, [selectedQuestionario, novoNome, onDuplicate]);

  const getStatusColor = useCallback((questionario: Questionario) => {
    if (questionario.inativo) return 'red';
    return 'green';
  }, []);

  const getStatusText = useCallback((questionario: Questionario) => {
    if (questionario.inativo) return 'Inativo';
    return 'Ativo';
  }, []);

  const getProgressPercent = useCallback((questionario: Questionario) => {
    let progresso = 0;
    const totalCampos = 4;

    if (questionario.nome) progresso += 1;
    if (questionario.abordagem) progresso += 1;
    if (questionario.areas && questionario.areas.length > 0) progresso += 1;
    if (!questionario.inativo) progresso += 1;

    return Math.round((progresso / totalCampos) * 100);
  }, []);

  const getClienteInfo = useCallback((questionario: Questionario) => {
    if (typeof questionario.cliente_id === 'object' && questionario.cliente_id !== null) {
      const clienteObj = questionario.cliente_id as { nome?: string; razao_social?: string; _id: string };
      return clienteObj.nome || clienteObj.razao_social || 'Cliente';
    }
    return 'Cliente não identificado';
  }, []);

  const formatAbordagem = useCallback((abordagem?: string) => {
    if (!abordagem) return 'Não definida';
    return abordagem.charAt(0).toUpperCase() + abordagem.slice(1);
  }, []);

  const formatAreaName = useCallback((area: any) => {
    if (typeof area === 'string') return area;
    if (area && typeof area === 'object') {
      return (area as any).description || (area as any).nome || 'Área não especificada';
    }
    return 'Área não especificada';
  }, []);

  const getMenuItems = useCallback((questionario: Questionario) => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'Visualizar Detalhes',
      onClick: () => handleView(questionario)
    },
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Editar',
      onClick: () => onEdit(questionario)
    },
    {
      key: 'version',
      icon: <FileTextOutlined />,
      label: 'Nova Versão',
      onClick: () => onCreateVersion(questionario._id)
    },
    ...(onDuplicate ? [{
      key: 'duplicate',
      icon: <CopyOutlined />,
      label: 'Duplicar',
      onClick: () => handleDuplicate(questionario)
    }] : []),
    {
      type: 'divider' as const
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Excluir',
      danger: true,
      onClick: () => handleDelete(questionario)
    }
  ], [handleView, onEdit, onCreateVersion, onDuplicate, handleDuplicate, handleDelete]);

  if (loading) {
    return (
      <div>
        {clienteId && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Skeleton active paragraph={{ rows: 1 }} />
          </Card>
        )}
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <Skeleton active />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (questionarios.length === 0) {
    return (
      <div>
        <Alert
          message={clienteId ? `Nenhum Questionário para ${clienteNome || 'Cliente'}` : "Nenhum Questionário Encontrado"}
          description={
            clienteId 
              ? `Não foram encontrados questionários para ${clienteNome || 'este cliente'}.`
              : "Nenhum questionário foi encontrado. Comece criando seu primeiro questionário de mapeamento LGPD."
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Empty
          image={<FileTextOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />}
          description={
            <div>
              <Text strong>
                {clienteId ? 'Nenhum questionário encontrado' : 'Nenhum questionário cadastrado'}
              </Text>
              <br />
              <Text type="secondary">
                {clienteId 
                  ? 'Cadastre questionários para este cliente.'
                  : 'Comece criando seu primeiro questionário de mapeamento LGPD.'
                }
              </Text>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <>
      <div>
        {/* Lista de questionários */}
        <div className="space-y-4">
          {questionarios.map((questionario) => (
            <Card
              key={questionario._id}
              hoverable
              actions={[
                <Tooltip title="Visualizar Detalhes" key="view">
                  <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(questionario)}
                  />
                </Tooltip>,
                <Tooltip title="Editar" key="edit">
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => onEdit(questionario)}
                  />
                </Tooltip>,
                <Tooltip title="Nova Versão" key="version">
                  <Button
                    type="text"
                    icon={<FileTextOutlined />}
                    onClick={() => onCreateVersion(questionario._id)}
                  />
                </Tooltip>,
                <Dropdown
                  menu={{ items: getMenuItems(questionario) }}
                  trigger={['click']}
                  key="more"
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                  />
                </Dropdown>
              ]}
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                  <Title level={4} style={{ margin: 0 }}>
                    {questionario.nome}
                  </Title>
                  <Tag color={getStatusColor(questionario)}>
                    {getStatusText(questionario)}
                  </Tag>
                </div>

                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space wrap>
                    <Space size="small">
                      <CalendarOutlined />
                      <Text type="secondary">
                        {new Date(questionario.created_at).toLocaleDateString('pt-BR')}
                      </Text>
                    </Space>

                    {questionario.abordagem && (
                      <Tag color={questionario.abordagem === 'detalhada' ? 'blue' : 'green'}>
                        {formatAbordagem(questionario.abordagem)}
                      </Tag>
                    )}

                    {questionario.cliente_id && !clienteId && (
                      <Space size="small">
                        <UserOutlined />
                        <Text type="secondary">
                          {getClienteInfo(questionario)}
                        </Text>
                      </Space>
                    )}
                  </Space>

                  <Space wrap>
                    {questionario.areas && questionario.areas.length > 0 && (
                      <Text type="secondary">
                        📍 {questionario.areas.length} área(s) mapeada(s)
                      </Text>
                    )}

                    {questionario.updated_at && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Atualizado em {new Date(questionario.updated_at).toLocaleDateString('pt-BR')}
                      </Text>
                    )}
                  </Space>

                  {/* Progress de conformidade */}
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Progresso do mapeamento
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {getProgressPercent(questionario)}%
                      </Text>
                    </div>
                    <Progress 
                      percent={getProgressPercent(questionario)} 
                      size="small"
                      status={questionario.inativo ? 'exception' : 'active'}
                      strokeColor={
                        getProgressPercent(questionario) >= 90 ? '#52c41a' :
                        getProgressPercent(questionario) >= 70 ? '#1890ff' :
                        getProgressPercent(questionario) >= 50 ? '#faad14' : '#ff4d4f'
                      }
                    />
                  </div>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Modal de Visualização */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            Detalhes do Questionário
            {clienteId && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {clienteNome || 'Cliente'}
              </Tag>
            )}
          </Space>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedQuestionario(null);
        }}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Fechar
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              if (selectedQuestionario) {
                onEdit(selectedQuestionario);
                setViewModalVisible(false);
              }
            }}
          >
            Editar
          </Button>
        ]}
        width={800}
      >
        {selectedQuestionario && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Informações de contexto do cliente */}
            {clienteId && (
              <Alert
                message={`Questionário vinculado ao cliente: ${clienteNome || 'Cliente selecionado'}`}
                type="info"
                showIcon={false}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Status e progresso em destaque */}
            <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {selectedQuestionario.inativo ? (
                    <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 16 }} />
                  ) : (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                  )}
                  <span>
                    <strong>Status:</strong> {getStatusText(selectedQuestionario)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '12px' }}>Progresso:</span>
                  <Progress 
                    type="circle" 
                    percent={getProgressPercent(selectedQuestionario)} 
                    width={40}
                    strokeColor={
                      getProgressPercent(selectedQuestionario) >= 70 ? '#52c41a' :
                      getProgressPercent(selectedQuestionario) >= 50 ? '#faad14' : '#ff4d4f'
                    }
                  />
                </div>
              </div>
            </Card>

            {/* Informações básicas */}
            <Descriptions title="📋 Informações Básicas" bordered column={1}>
              <Descriptions.Item label="Nome">
                {selectedQuestionario.nome}
              </Descriptions.Item>
              <Descriptions.Item label="Abordagem">
                <Tag color={selectedQuestionario.abordagem === 'detalhada' ? 'blue' : 'green'}>
                  {formatAbordagem(selectedQuestionario.abordagem)}
                </Tag>
              </Descriptions.Item>
              {selectedQuestionario.cliente_id && (
                <Descriptions.Item label="Cliente">
                  <Space>
                    <UserOutlined />
                    {getClienteInfo(selectedQuestionario)}
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Data de Criação">
                {new Date(selectedQuestionario.created_at).toLocaleString('pt-BR')}
              </Descriptions.Item>
              {selectedQuestionario.updated_at && (
                <Descriptions.Item label="Última Atualização">
                  {new Date(selectedQuestionario.updated_at).toLocaleString('pt-BR')}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider />

            {/* Áreas mapeadas */}
            <div>
              <Title level={5}>
                <BuildOutlined /> Áreas Mapeadas ({selectedQuestionario.areas?.length || 0})
              </Title>
              {selectedQuestionario.areas && selectedQuestionario.areas.length > 0 ? (
                <List
                  size="small"
                  dataSource={selectedQuestionario.areas}
                  renderItem={(area, index) => (
                    <List.Item>
                      <Space>
                        <span style={{ color: '#666', fontSize: '12px' }}>#{index + 1}</span>
                        <span>{formatAreaName(area)}</span>
                      </Space>
                    </List.Item>
                  )}
                />
              ) : (
                <Alert
                  message="Nenhuma área mapeada"
                  description="Este questionário ainda não possui áreas vinculadas. Edite o questionário para adicionar áreas."
                  type="info"
                  showIcon
                />
              )}
            </div>

            {/* Resumo de progresso */}
            <Card size="small" title="📊 Resumo de Progresso">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Nome definido:</span>
                  <Tag color={selectedQuestionario.nome ? 'green' : 'red'}>
                    {selectedQuestionario.nome ? 'Sim' : 'Não'}
                  </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Abordagem definida:</span>
                  <Tag color={selectedQuestionario.abordagem ? 'green' : 'red'}>
                    {selectedQuestionario.abordagem ? 'Sim' : 'Não'}
                  </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Áreas mapeadas:</span>
                  <Tag color={selectedQuestionario.areas && selectedQuestionario.areas.length > 0 ? 'green' : 'red'}>
                    {selectedQuestionario.areas?.length || 0} área(s)
                  </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Status ativo:</span>
                  <Tag color={!selectedQuestionario.inativo ? 'green' : 'red'}>
                    {!selectedQuestionario.inativo ? 'Sim' : 'Não'}
                  </Tag>
                </div>
              </Space>
            </Card>

            {/* Recomendações */}
            <Card size="small" title="💡 Recomendações">
              <Space direction="vertical" size="small">
                {!selectedQuestionario.abordagem && (
                  <Tag color="orange">📋 Defina a abordagem do questionário</Tag>
                )}
                {(!selectedQuestionario.areas || selectedQuestionario.areas.length === 0) && (
                  <Tag color="blue">🏢 Adicione áreas para mapear</Tag>
                )}
                {selectedQuestionario.inativo && (
                  <Tag color="red">⚠️ Questionário está inativo</Tag>
                )}
                {getProgressPercent(selectedQuestionario) === 100 && (
                  <Tag color="green">✅ Questionário está completo</Tag>
                )}
              </Space>
            </Card>
          </Space>
        )}
      </Modal>

      {/* Modal de Duplicação */}
      <Modal
        title="Duplicar Questionário"
        open={duplicateModalVisible}
        onOk={handleConfirmDuplicate}
        onCancel={() => {
          setDuplicateModalVisible(false);
          setSelectedQuestionario(null);
          setNovoNome('');
        }}
        confirmLoading={duplicating}
        okText="Duplicar"
        cancelText="Cancelar"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Duplicando: <strong>{selectedQuestionario?.nome}</strong>
          </Text>
          {clienteId && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Cliente: {clienteNome || 'Cliente selecionado'}
            </Text>
          )}
          <Input
            placeholder="Nome do novo questionário"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            maxLength={100}
            showCount
          />
        </Space>
      </Modal>
    </>
  );
});

QuestionarioList.displayName = 'QuestionarioList';

export default QuestionarioList;